import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@mui/material";
import { getProfile } from "../redux/asyncThunk/userActions";
import formatDateToDDMMYYYY, { formatDateTimeRu } from "../utils/formatDate";
import AppPageShell from "./AppPageShell";
import { userSlice } from "../redux/user/userSlice";
import ProfileChangePassword from "./profile/ProfileChangePassword";
import ProfileAccessKeys from "./profile/ProfileAccessKeys";
import {
  formatPaymentPrice,
  getPaymentPackageLabel,
  getUserRoleLabel,
} from "../utils/userProfile";

const { showDialog } = userSlice;

function ProfileInfoRow({ label, value, valueClassName }) {
  return (
    <div className="app-profile-info-list__row">
      <span>{label}</span>
      <strong className={valueClassName}>{value}</strong>
    </div>
  );
}

export default function Profile() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.data);
  const profile = useSelector((state) => state.user?.profile);
  const keys = useSelector((state) => state.user?.keys);
  const games = useSelector((state) => state.user?.games);

  useEffect(() => {
    if (user?.user.id) {
      dispatch(getProfile({ id: user.user.id }));
    }
  }, [dispatch, user?.user.id]);

  const email = profile?.login ?? user?.user?.login ?? "—";
  const role = getUserRoleLabel(profile?.type ?? user?.user?.type);
  const scenariosCount = games?.length ?? 0;
  const emailConfirmed = profile?.email?.confirm ? "Да" : "Нет";
  const telegramLinked = profile?.tgId?.confirm
    ? "Привязан"
    : profile?.tgId?.confirmCode
      ? "Ожидает"
      : "Нет";
  const registeredAt = profile?.createdAt
    ? formatDateToDDMMYYYY(profile.createdAt)
    : "—";
  const payments = profile?.payments ?? [];
  const hasKeys = Boolean(keys?.length);

  return (
    <AppPageShell title="Профиль" className="app-page--profile">
      <div className="app-profile-layout">
        <div className="app-profile-top-row">
          <section className="app-profile-card app-profile-card--user">
            <h3 className="app-profile-card__title">О пользователе</h3>

            <div className="app-profile-info-list">
              <ProfileInfoRow label="Электронная почта" value={email} />
              <ProfileInfoRow label="ID аккаунта" value={user?.user?.id ?? "—"} />
              <ProfileInfoRow label="Роль" value={role} />
              <ProfileInfoRow label="Дата регистрации" value={registeredAt} />
              <ProfileInfoRow
                label="Почта подтверждена"
                value={emailConfirmed}
                valueClassName={
                  profile?.email?.confirm
                    ? "app-profile-info-row__value--ok"
                    : "app-profile-info-row__value--muted"
                }
              />
              <ProfileInfoRow label="Telegram" value={telegramLinked} />
              <ProfileInfoRow
                label="Сценариев"
                value={String(scenariosCount)}
              />
              <ProfileInfoRow
                label="Оплаченных заказов"
                value={String(payments.length)}
              />
              <ProfileInfoRow
                label="Контент 18+"
                value={
                  profile?.accessAdultContent ? "Подтверждён" : "Не подтверждён"
                }
              />
            </div>
          </section>

          <section className="app-profile-card app-profile-card--security">
            <h3 className="app-profile-card__title">Безопасность</h3>
            <ProfileChangePassword userId={user?.user?.id} />
          </section>
        </div>

        <div className="app-profile-grid app-profile-grid--bottom">
          <section className="app-profile-card app-profile-card--compact">
            <div className="app-profile-card__title-row">
              <h3 className="app-profile-card__title">История оплат</h3>
              {payments.length === 0 ? (
                <Link to="/main/price" className="app-profile-inline-link">
                  Тарифы
                </Link>
              ) : null}
            </div>

            {payments.length === 0 ? (
              <p className="app-profile-muted">Пока нет оплаченных заказов</p>
            ) : (
              <div className="app-table-wrap app-profile-payments">
                <table className="app-table app-table--compact">
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Сумма</th>
                      <th>Пакет</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id}>
                        <td>
                          {payment.created_at
                            ? formatDateTimeRu(payment.created_at)
                            : "—"}
                        </td>
                        <td>{formatPaymentPrice(payment.price)}</td>
                        <td>
                          {getPaymentPackageLabel(
                            payment.packageSize,
                            payment.packageId,
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {hasKeys ? (
            <ProfileAccessKeys keys={keys} games={games} compact />
          ) : null}
        </div>

        <div className="app-profile-footer-bar">
          <Button
            color="error"
            size="small"
            variant="outlined"
            className="app-profile-delete-btn"
            onClick={() => dispatch(showDialog("delete-accaunt"))}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Удалить аккаунт
          </Button>
        </div>
      </div>
    </AppPageShell>
  );
}
